/**
 * Helpers shared by the GDPR document families (personuppgift- and
 * samtycke-bilagor and their standalone avtal siblings).
 *
 * These documents are short, auto-numbered (§ 1 … § n) sections of frozen
 * text with bullet lists, plus a few "Label: value" panels whose values are
 * plain slot strings — never formatted from the compile context.
 */
import { list, p, section } from '@hyranai/regta-document-ir';
import { fieldRows, partySlot, textVal } from '../common/index.js';
export const legalBase = (source) => ({
    plane: 'legal',
    status: 'published',
    language: 'sv-SE',
    legalReview: { reviewedBy: 'Haymanot', source },
});
const partNodes = (body) => body.map((part) => (typeof part === 'string' ? p(part) : list([...part.bullets])));
/**
 * A frozen, auto-numbered section: the § number is the section's position,
 * exactly as the legacy `SectionNumberingProvider` computed it.
 */
export function autoClause(spec) {
    return {
        ...legalBase(spec.source),
        blockKey: spec.blockKey,
        version: 'v1',
        title: spec.title,
        summary: spec.summary,
        slots: [],
        compile: () => [section(spec.ref, spec.title, partNodes(spec.body))],
    };
}
/** A frozen paragraph with no section of its own. */
export function staticParagraph(spec) {
    return {
        ...legalBase(spec.source),
        blockKey: spec.blockKey,
        version: 'v1',
        title: spec.title,
        summary: spec.summary,
        slots: [],
        compile: () => [p(spec.text, spec.variant ? { variant: spec.variant } : {})],
    };
}
/**
 * A party datum on a GDPR document: autofilled from the `(role, 0)` party
 * on the standalone, overlaid from the parent lease on the bilaga.
 */
export function gdprPartySlot(variant, spec) {
    const type = spec.field === 'address' ? 'textarea' : 'text';
    const extra = { type, ...(spec.required ? { required: true } : {}) };
    if (variant === 'standalone') {
        return partySlot(spec.key, spec.label, spec.role, 0, spec.field, { ...extra, ...(spec.note ? { help: spec.note } : {}) });
    }
    const fromParent = spec.fromParent !== false;
    const help = [fromParent ? 'Hämtas från hyresavtalet.' : null, spec.note ?? null].filter(Boolean).join(' ');
    return {
        key: spec.key,
        label: spec.label,
        ...extra,
        ...(fromParent ? { derivedFromParent: true } : {}),
        ...(help ? { help } : {}),
    };
}
/** The "Label | value" panel rows of a GDPR document; values keep their line breaks. */
export const gdprRows = (input, rows) => fieldRows(rows.map(([label, key]) => [label, [textVal(input, key, 'long')]]));
//# sourceMappingURL=text.js.map